import { AnalyticsService } from "mis-crystal-design-system/analytics";
/** @format */

import { ConnectionPositionPair, Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { switchMap, take } from "rxjs/operators";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../../constants/assets.constants";
import { NsEmpWebExpData } from "../../../models/emp-web-exp.model";
import { BookingDataService } from "../../../services/booking-data.service";
import { Observable, Subject, Subscription } from "rxjs";
import { MenuDirective } from "../../../modules/menu/menu.directive";
import { ConfigurationService } from "../../../services/configuration.service";
import { BsModalService } from "mis-design-system/modal";
import { CreateBookingComponent } from "../../modals/create-booking/create-booking.component";
import { NsExpWidget } from "../../../models/widgets.models";
import { AsyncWidget } from "mis-crystal-design-system/widgets";

@Component({
  selector: "mis-employee-web-exp-booking-card",
  templateUrl: "./booking-card.component.html",
  styleUrls: ["./booking-card.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookingCardComponent implements OnInit {
  Assets = CONSTANTS_ASSETS_EMP_WEB_EXP;
  @Input() noBookingText: string;
  @Input() showCreateButton: boolean;
  @Input() dateFormat: string;
  @Input() timeFormat: string;
  @Input() timezone: string;
  @Output() create = new EventEmitter<"OFFICE" | "WFH">(true);
  @Output() cardAction = new EventEmitter<{ type: NsEmpWebExpData.TCardActions; booking?: NsEmpWebExpData.IBookingDetailDTOS }>(true);
  @Output() checkin = new EventEmitter<NsEmpWebExpData.IBookingDetailDTOS>(true);
  @Output() checkout = new EventEmitter<NsEmpWebExpData.IBookingDetailDTOS>(true);
  @ViewChild("menuOrigin", { read: MenuDirective }) menuOrigin: MenuDirective;
  endObs = new Subject<void>();
  overlayRef: OverlayRef;
  bookingPermissionStream: Observable<NsExpWidget.IBookingValidity>;
  configStream = this.configSvc.configurations;
  bookingConfigStream = this.configSvc.bookingConfigurations;

  officeBookingAllowed: boolean = false;
  remoteBookingAllowed: boolean = false;
  onlyParkingBookingEnabled: boolean = false;

  private bookingConfigStreamSubscription: Subscription;

  @Input() widgetControl: AsyncWidget;

  constructor(
    public bookingSvc: BookingDataService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    public configSvc: ConfigurationService,
    public modal: BsModalService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.bookingPermissionStream = this.widgetControl.value.pipe(switchMap(booking => this.configSvc.applyOfficeConfigurations(booking)));
    this.bookingConfigStreamSubscription = this.configStream.subscribe(res => {
      this.officeBookingAllowed = res.BUSettings.features[13].createBookingEnabled;
      this.remoteBookingAllowed = res.flattenedSettings.wfhBookingAllowed;
      this.onlyParkingBookingEnabled = !!res?.flattenedSettings?.onlyParkingBookingEnabled
        ? res?.flattenedSettings?.onlyParkingBookingEnabled
        : false;
    });
  }

  checkOnlyParkingEnabled(requestType: string) {
    return requestType !== "OFFICE" ? true : !(!!this.onlyParkingBookingEnabled && this.onlyParkingBookingEnabled);
  }

  showMenu(template: TemplateRef<ElementRef>, origin: HTMLElement): void {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions([
        new ConnectionPositionPair({ originX: "end", originY: "bottom" }, { overlayX: "end", overlayY: "top" }),
        new ConnectionPositionPair({ originX: "start", originY: "top" }, { overlayX: "start", overlayY: "bottom" })
      ])
      .withPush(true);
    const config = new OverlayConfig({
      hasBackdrop: true,
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      backdropClass: "cdk-overlay-transparent-backdrop"
    });
    this.overlayRef = this.overlay.create(config);
    this.overlayRef.attach(new TemplatePortal(template, this.viewContainerRef));
    // componentInstance.instance
    this.overlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(res => {
        this.overlayRef.detach();
        this.overlayRef.dispose();
      });
  }

  closeMenu(type: "Edit" | "Cancel" | "Convert", booking): void {
    this.cardAction.emit({ type, booking });
    this.overlayRef.detach();
    this.overlayRef.dispose();
  }

  handleCardAction(bookingData): void {
    if (bookingData.requestStatus === "CONFIRMED") {
      this.checkin.emit(bookingData);
    } else if (bookingData.requestStatus === "SIGNED_IN") {
      this.checkout.emit(bookingData);
    }
  }

  quickActionEvent(id: "OFFICE" | "WFH"): void {
    this.menuOrigin?.close();
    this.create.emit(id);
  }

  openBooking() {
    if (!this.remoteBookingAllowed && this.officeBookingAllowed) {
      this.quickActionEvent("OFFICE");
    } else if (!this.officeBookingAllowed && this.remoteBookingAllowed) {
      this.quickActionEvent("WFH");
    }
  }

  openMobileViewBooking() {
    this.configStream.pipe(take(1)).subscribe(cfg => {
      const initialState = {
        animated: false,
        config: cfg
      };
      this.modal.show(CreateBookingComponent, {
        ...initialState,
        initialState
      });
    });
    this.analytics.send("emp_web_today_bookings", "emp_web", "click", "mobileView");
  }
  ngOnDestroy() {
    this.bookingConfigStreamSubscription.unsubscribe();
  }
}
